import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, tzHeadersSchema, sseQuerySchema } from '../schemas';
import { safetyEventSchema } from '@shared/interfaces/ISafetyEvent';
import { SafetyEventStatuses } from '@shared/constants/statuses.const';
import { tradeSchema } from '@shared/interfaces/ITrade';
import { minimalSafetyEventForClientSchema } from '@shared/interfaces/IMinimalSafetyEventForClient';

const projectTagParamsSchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
	tagId: requiredStringSchema.describe('Tag identifier'),
});
const timestampQuerySchema = z.object({ timestamp: requiredStringSchema });

const safetyEventParamsSchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
	safetyEventId: requiredStringSchema.describe('Safety event identifier'),
});

/**
 * sosAlert & restrictedArea ingest an `IMergedTag`, a generic discriminated union
 * (IWorkerMergedTag<T> | IManagerMergedTag | IUtilityMergedTag<T>) too deeply
 * nested to express as one Zod schema — hence `looseObject`, which validates the
 * field the pipeline requires and passes the rest of the merged tag through.
 *
 * `projectId` is deliberately NOT declared here even though every caller sends
 * one: declaring it in the body as well as the path would give the request two
 * project keys, which is the "two places at once" shape the audit rejects and
 * CLAUDE.md forbids outright ("never in the body, at any depth"). The merged
 * tag's own `projectId` still arrives — `looseObject` passes it through, and
 * `handlePromptlyMergedTagSafetyEvent` still reads it as the domain field it is
 * — and the route asserts it equals the path param, 400ing loudly if not. So
 * the field survives as data while the path is the only declared claim.
 */
const mergedTagIngestBodySchema = z.looseObject({
	tagId: requiredStringSchema.describe('Tag identifier'),
});

// `projectId` is omitted, not merely ignored: a safety event cannot be
// relocated between projects by an update. `_id` likewise — the id is the
// address, never the payload.
const safetyEventUpdateBodySchema = safetyEventSchema.omit({ _id: true, projectId: true }).partial();

export const safetyContract = defineContract(
	'safety',
	{
		getSafetyEventsToday: route({
			method: 'GET',
			path: '/projects/:projectId/safetyEvents/today',
			params: projectIdParamsSchema,
			// The enum, not `z.string()`. d-orca was sending `status=open`, which is
			// not a member, and the handler passed it straight into the Mongo filter
			// — the call returned [] unconditionally instead of 400ing.
			query: z.object({ status: z.enum(SafetyEventStatuses).optional() }),
			headers: tzHeadersSchema,
			response: z.array(safetyEventSchema),
			operationId: 'getSafetyEventsToday',
			tags: ['Safety'],
			summary: "Get today's safety events",
		}),
		getSafetyEventsByIds: route({
			method: 'GET',
			path: '/projects/:projectId/safetyEvents',
			params: projectIdParamsSchema,
			// An `ids` FILTER on the collection, not a `/byIds` sub-path: the
			// playbook prefers a filter over a nested-resource costume, and the
			// project the read is scoped by is the path one either way.
			query: z.object({ ids: requiredStringSchema }),
			response: z.array(safetyEventSchema),
			operationId: 'getSafetyEventsByIds',
			tags: ['Safety'],
			summary: 'Get safety events by IDs within a project',
		}),
		getRangeSafetyEvents: route({
			method: 'GET',
			path: '/projects/:projectId/safetyEvents/range',
			params: projectIdParamsSchema,
			query: z.object({
				startRangeTimestamp: z.coerce.number(),
				endRangeTimestamp: z.coerce.number(),
				areaId: z.string().optional(),
				floorId: z.string().optional(),
			}),
			response: z.array(safetyEventSchema),
			operationId: 'getRangeSafetyEvents',
			tags: ['Safety'],
			summary: 'Get safety events in a time range',
		}),
		safetyEventClicked_BI: route({
			method: 'POST',
			// `<ROUTER_PATH>/BI/<BI_EVENT_NAME>` per CLAUDE.md's BI convention,
			// with the project in the path: the handler performs a scoped READ of
			// the safety event before emitting, so this is project data and the BI
			// shape does not exempt it.
			path: '/projects/:projectId/BI/safetyEventClicked',
			params: projectIdParamsSchema,
			body: z.object({
				safetyEventId: requiredStringSchema,
				source: requiredStringSchema,
				eventsOpenedCounter: z.number(),
			}),
			headers: tzHeadersSchema,
			response: z.string(),
			operationId: 'safetyEventClickedBI',
			tags: ['Safety'],
			summary: 'Track BI event for safety event click',
		}),
		closeSafetyEvent: route({
			method: 'PUT',
			path: '/projects/:projectId/safetyEvents/:safetyEventId/close',
			params: safetyEventParamsSchema,
			headers: tzHeadersSchema,
			response: minimalSafetyEventForClientSchema,
			operationId: 'closeSafetyEvent',
			tags: ['Safety'],
			summary: 'Close a safety event',
		}),
		updateSafetyEventById: route({
			method: 'PUT',
			path: '/projects/:projectId/safetyEvents/:safetyEventId',
			params: safetyEventParamsSchema,
			body: safetyEventUpdateBodySchema,
			response: z.string(),
			operationId: 'updateSafetyEventById',
			tags: ['Safety'],
			summary: 'Update a safety event by ID',
		}),
		updateTrade: route({
			method: 'PUT',
			path: '/updateTrade',
			body: tradeSchema,
			response: z.string(),
			operationId: 'updateTrade',
			tags: ['Safety'],
			summary: 'Update trade in safety events',
		}),
		syncSafetyData: route({
			method: 'POST',
			path: '/projects/:projectId/sync',
			params: projectIdParamsSchema,
			headers: tzHeadersSchema,
			response: z.string(),
			operationId: 'syncSafetyData',
			tags: ['Safety'],
			summary: 'Synchronize safety data',
		}),
		sosAlert: route({
			method: 'POST',
			path: '/projects/:projectId/safetyEvents/sos',
			params: projectIdParamsSchema,
			body: mergedTagIngestBodySchema,
			headers: tzHeadersSchema,
			response: safetyEventSchema.or(z.string()),
			operationId: 'sosAlert',
			tags: ['Safety'],
			summary: 'Create SOS alert event',
		}),
		restrictedArea: route({
			method: 'POST',
			path: '/projects/:projectId/safetyEvents/restrictedArea',
			params: projectIdParamsSchema,
			body: mergedTagIngestBodySchema,
			headers: tzHeadersSchema,
			response: safetyEventSchema.or(z.string()),
			operationId: 'restrictedArea',
			tags: ['Safety'],
			summary: 'Create restricted area violation event',
		}),
		getSafetyEventsByTagIdInOneHourTimeframe: route({
			method: 'GET',
			path: '/projects/:projectId/tags/:tagId/safetyEvents/hourTimeframe',
			params: projectTagParamsSchema,
			query: timestampQuerySchema,
			response: z.array(safetyEventSchema),
			operationId: 'getSafetyEventsByTagIdInOneHourTimeframe',
			tags: ['Safety'],
			summary: 'Get safety events for a tag in a one-hour timeframe',
		}),
		getSafetyEventsByTagIdInOneDayTimeframe: route({
			method: 'GET',
			path: '/projects/:projectId/tags/:tagId/safetyEvents/dayTimeframe',
			params: projectTagParamsSchema,
			query: timestampQuerySchema,
			headers: tzHeadersSchema,
			response: z.array(safetyEventSchema),
			operationId: 'getSafetyEventsByTagIdInOneDayTimeframe',
			tags: ['Safety'],
			summary: 'Get safety events for a tag in a one-day timeframe',
		}),
	},
	{ root: true }
);

export const safetyStreamRoute = route({
	method: 'GET',
	path: '/stream',
	query: sseQuerySchema,
	response: z.any().describe('SSE stream of safety events'),
	operationId: 'getSafetyEventsStream',
	tags: ['Safety'],
	summary: 'Stream safety events (SSE)',
});
